import React from "react";
import DataTable, { createTheme } from "react-data-table-component";
import { ActionMenu, BaseButton, IUser, bytesToHumanReadableSize, colors } from "../../../../libs";

import * as Styled from "./UsersList.styled";

import { Alert, Modal } from "react-bootstrap";
import { useAuth } from "../../../../behavioral";
import { GeneratePaymentLinkModal } from "../generate-payment-link-modal";
import { UpdateManualPaymentModal } from "../update-manual-payment-modal";
import { XLg } from "react-bootstrap-icons";

interface IProps {
  users: IUser[];
  onRefresh: () => void | Promise<void>;
}

const customStyles = {
  headRow: {
    style: {
      border: "none",
    },
  },
  headCells: {
    style: {
      color: colors.text.white,
      fontSize: "18px",
      fontWeight: "bold",
    },
  },
  rows: {
    highlightOnHoverStyle: {
      backgroundColor: "rgb(230, 244, 244)",
      borderBottomColor: "#FFFFFF",
      borderRadius: "25px",
      outline: "1px solid #FFFFFF",
    },
  },
  pagination: {
    style: {
      border: "none",
    },
  },
};

export const UsersList: React.FC<IProps> = ({ users, onRefresh }) => {
  const { resendConfirmationCode } = useAuth();
  const [resendConfirmationSuccessMessage, setResendConfirmationSuccessMessage] =
    React.useState("");
  const [resendConfirmCodeError, setResendConfirmCodeError] = React.useState("");

  const [showGeneratePaymentLinkModal, setShowGeneratePaymentLinkModal] = React.useState(false);
  const [showUpdateManualPaymentModal, setShowUpdateManualPaymentModal] = React.useState(false);
  const [selectedUser, setSelectedUser] = React.useState<IUser | null>(null);
  const [showActAsUserModal, setShowActAsUserModal] = React.useState(false);

  React.useEffect(() => {
    if (showActAsUserModal) {
      const timer = setTimeout(() => {
        window.location.reload();
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [showActAsUserModal]);

  const handleResendConfirmCode = React.useCallback(
    async (user: IUser) => {
      setResendConfirmCodeError("");
      setResendConfirmationSuccessMessage("");
      try {
        await resendConfirmationCode(user.email);
        setResendConfirmationSuccessMessage("Email confirmation sent successfully!");
      } catch (err) {
        console.log(err);
        setResendConfirmCodeError(
          "There is an error while sending the confirmation code to email: " + user.email
        );
      }
    },
    [resendConfirmationCode]
  );

  const handleGeneratePaymentLink = React.useCallback((user: IUser) => {
    setSelectedUser(user);
    setShowGeneratePaymentLinkModal(true);
  }, []);

  const handleUpdateManualPayment = React.useCallback((user: IUser) => {
    setSelectedUser(user);
    setShowUpdateManualPaymentModal(true);
  }, []);

  const columns = [
    {
      name: "Email",
      selector: (row: IUser) => row.email,
      sortable: true,
    },
    {
      name: "Name",
      selector: (row: IUser) => row.name,
      sortable: true,
      width: "200px",
    },
    {
      name: "Client ID",
      selector: (row: IUser) => row.userId,
      sortable: true,
      width: "350px",
    },
    {
      name: "Phone",
      selector: (row: IUser) => row.phone,
      sortable: false,
      width: "150px",
    },
    {
      name: "Storage",
      selector: (row: IUser) => bytesToHumanReadableSize(row.storage?.used || 0),
      sortable: true,
      width: "120px",
    },
    {
      name: "Payment",
      cell: (row: IUser) => (
        <BaseButton onClick={() => handleUpdateManualPayment(row)}>
          {row.subscriptionInfo?.billingMode === "MANUAL"
            ? "Manual"
            : row.subscriptionInfo?.status === "PAYMENT_COMPLETED"
              ? "Stripe"
              : "Manage"}
        </BaseButton>
      ),
      width: "120px",
    },
    {
      name: "Act as User",
      cell: (row: IUser) => (
        <BaseButton
          onClick={() => {
            setSelectedUser(row);
            setShowActAsUserModal(true);
            localStorage.setItem("actAsUserId", row.userId);
          }}
        >
          Act
        </BaseButton>
      ),
      width: "130px",
    },
    {
      cell: (row: IUser) => (
        <ActionMenu
          menu={{
            resend: {
              label: "Resend Confirm code",
              onClickHandler: () => handleResendConfirmCode(row),
            },
            payment: {
              label: "Generate Payment Link",
              onClickHandler: () => handleGeneratePaymentLink(row),
            },
            manualPayment: {
              label: "Manage Billing",
              onClickHandler: () => handleUpdateManualPayment(row),
            },
          }}
        />
      ),
      allowOverflow: true,
      button: true,
      width: "56px",
    },
  ];

  createTheme("dark", {
    background: {
      default: "transparent",
    },
  });

  return (
    <Styled.ManageStorageWrapper fluid>
      <Styled.Header>Manage Users</Styled.Header>
      {resendConfirmCodeError && (
        <Alert variant="danger" dismissible>
          {resendConfirmCodeError}
        </Alert>
      )}

      {resendConfirmationSuccessMessage && (
        <Alert dismissible>{resendConfirmationSuccessMessage}</Alert>
      )}

      <Styled.StyledTable>
        <DataTable
          columns={columns}
          data={users}
          pagination={true}
          paginationPerPage={10}
          customStyles={customStyles}
          theme="dark"
          pointerOnHover
        />
      </Styled.StyledTable>
      {showGeneratePaymentLinkModal && selectedUser && (
        <GeneratePaymentLinkModal
          user={selectedUser}
          show={showGeneratePaymentLinkModal}
          onHide={() => setShowGeneratePaymentLinkModal(false)}
          centered
        />
      )}
      {showUpdateManualPaymentModal && selectedUser && (
        <UpdateManualPaymentModal
          user={selectedUser}
          show={showUpdateManualPaymentModal}
          onHide={() => setShowUpdateManualPaymentModal(false)}
          onRecorded={onRefresh}
          centered
        />
      )}
      <Modal show={showActAsUserModal} onHide={() => setShowActAsUserModal(false)} centered>
        <Styled.ModalWrapper>
          <Styled.ModalTitleWrapper>
            <Styled.ModalTitle>Act as User</Styled.ModalTitle>
            <XLg style={{ cursor: "pointer" }} onClick={() => setShowActAsUserModal(false)} />
          </Styled.ModalTitleWrapper>
          <Modal.Body>
            <Styled.ActAsUserModalContent>
              <Styled.ActAsUserDescription>You are now acting as:</Styled.ActAsUserDescription>
              <Styled.ActAsUserEmail>{selectedUser?.email}</Styled.ActAsUserEmail>
              <Styled.ActAsUserRefreshNote>
                Page will refresh in 5 seconds...
              </Styled.ActAsUserRefreshNote>
            </Styled.ActAsUserModalContent>
          </Modal.Body>
        </Styled.ModalWrapper>
      </Modal>
    </Styled.ManageStorageWrapper>
  );
};
